import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { X } from '@lifeomic/chromicons';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  close: {
    id: 'phc-web-toolkit.generateDrawer.close',
    defaultMessage: 'Close',
  },
});

const DRAWER_WIDTH = 400;
const DRAWER_MIN_WIDTH = 280;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    // Target size is DRAWER_WIDTH, but shrink down to DRAWER_MIN_WIDTH
    // on narrow viewports so the drawer never pushes the editor off
    // screen (x-overflow on the article-report page on small laptops).
    flexBasis: theme.pxToRem(DRAWER_WIDTH),
    width: theme.pxToRem(DRAWER_WIDTH),
    minWidth: theme.pxToRem(DRAWER_MIN_WIDTH),
    maxWidth: '100%',
    flexShrink: 1,
    flexGrow: 0,
    height: '100%',
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      flexBasis: theme.pxToRem(320),
      width: theme.pxToRem(320),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
}));

type GenerateDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
};

export const GenerateDrawer: React.FC<GenerateDrawerProps> = ({
  isOpen,
  onClose,
  title,
  actions,
  children,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  if (!isOpen) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Text size="body" weight="bold">
          {title}
        </Text>
        <IconButton
          aria-label={formatMessage(messages.close)}
          icon={X}
          onClick={onClose}
          color="default"
          size={0}
        />
      </Box>
      <Box className={classes.body}>{children}</Box>
      {actions && <Box className={classes.footer}>{actions}</Box>}
    </Box>
  );
};
